import React, { useCallback, useEffect } from "react";

import { Decimal, Decimalish, LiquityStoreState, StabilityDeposit } from "@liquity/lib-base";
import { LiquityStoreUpdate, useLiquityReducer, useLiquitySelector } from "@liquity/lib-react";
import { useMyTransactionState } from "../Transaction";

import { StabilityDepositEditor } from "./StabilityDepositEditor";
import { useStabilityView } from "./context/StabilityViewContext";
import {
  selectForStabilityDepositChangeValidation,
  validateStabilityDepositChange
} from "./validation/validateStabilityDepositChange";
import { MyDeposit } from './PSY/MyDeposit'
import { TotalRewards } from './PSY/TotalRewards'
import { CollateralLexicons } from "src/lexicon"

// type StabilityDepositManagerState = ReturnType<typeof init>;
type StabilityDepositManagerState = {
  originalDeposit: StabilityDeposit
  editedSLSD: Decimal
  changePending: boolean
  collateralKey: string
}
type StabilityDepositManagerAction =
  | LiquityStoreUpdate
  | { type: "startChange" | "finishChange" | "revert" }
  | { type: "setDeposit"; newValue: Decimalish };

const reduceWith = (action: StabilityDepositManagerAction) => (
  state: StabilityDepositManagerState
): StabilityDepositManagerState => reduce(state, action);

const finishChange = reduceWith({ type: "finishChange" });
const revert = reduceWith({ type: "revert" });

const reduce = (
  state: StabilityDepositManagerState,
  action: StabilityDepositManagerAction
): StabilityDepositManagerState => {

  const { originalDeposit, editedSLSD, changePending, collateralKey } = state;

  switch (action.type) {
    case "startChange": {
      console.log("changeStarted");
      return { ...state, changePending: true };
    }

    case "finishChange":
      return { ...state, changePending: false };

    case "setDeposit":
      return { ...state, editedSLSD: Decimal.from(action.newValue) };

    case "revert":
      return { ...state, editedSLSD: originalDeposit.currentSLSD };

    case "updateStore": {
      const {
        stateChange: { collateralStates }
      } = action;

      if (!collateralStates || !collateralKey) {
        return state;
      }

      const collateralState = collateralStates[collateralKey]
      const { stabilityDeposit: updatedDeposit } = collateralState;

      if (!updatedDeposit) {
        return state;
      }

      const newState = { ...state, originalDeposit: updatedDeposit };

      const changeCommitted =
        !updatedDeposit.initialSLSD.eq(originalDeposit.initialSLSD) ||
        updatedDeposit.currentSLSD.gt(originalDeposit.currentSLSD) ||
        updatedDeposit.collateralGain.lt(originalDeposit.collateralGain)
        || updatedDeposit.psyReward.lt(originalDeposit.psyReward);

      if (changePending && changeCommitted) {
        return finishChange(revert(newState));
      }

      return {
        ...newState,
        editedSLSD: updatedDeposit.apply(originalDeposit.whatChanged(editedSLSD))
      };
    }
  }
};

const transactionId = "stability-deposit";

type Props = {
  spAddress: string
  collateralKey: string
}

export const StabilityDepositManager: React.FC<Props> = ({ spAddress, collateralKey }) => {
  const init = useCallback(({ collateralStates }: LiquityStoreState) => {
    const stabilityDeposit = collateralKey
      ? collateralStates[collateralKey!].stabilityDeposit
      : new StabilityDeposit(Decimal.ZERO, Decimal.ZERO, Decimal.ZERO, Decimal.ZERO)

    return {
      originalDeposit: stabilityDeposit,
      editedSLSD: stabilityDeposit.currentSLSD,
      changePending: false,
      collateralKey: collateralKey
    }
  }, [collateralKey])

  const [{ originalDeposit, editedSLSD, changePending }, dispatch] = useLiquityReducer(reduce, init);
  const validationContext = useLiquitySelector(selectForStabilityDepositChangeValidation(collateralKey));
  const { stabilityDeposit, price } = validationContext
  const { dispatchEvent } = useStabilityView();
  // TODO: move to AppStateContext
  const lexicon = CollateralLexicons.find(lexicon => lexicon.key === collateralKey)

  const [validChange, description] = validateStabilityDepositChange(
    originalDeposit,
    editedSLSD,
    validationContext,
    lexicon
  );

  const myTransactionState = useMyTransactionState(transactionId);
  const collateralGain = stabilityDeposit.collateralGain
  const collateralGainUSDValue = collateralGain.mul(price)

  useEffect(() => {
    if (
      myTransactionState.type === "waitingForApproval" ||
      myTransactionState.type === "waitingForConfirmation"
    ) {
      dispatch({ type: "startChange" });
    } else if (myTransactionState.type === "failed" || myTransactionState.type === "cancelled") {
      dispatch({ type: "finishChange" });
    } else if (myTransactionState.type === "confirmedOneShot") {
      dispatchEvent("DEPOSIT_CONFIRMED");
    }
  }, [myTransactionState.type, dispatch, dispatchEvent]);

  return (
    <div className="bg-component p-5 rounded-xl border-component flex flex-col">
      <div className="flex flex-col md:flex-row justify-between">
        <MyDeposit className="min-w-[300px]"/>
        {!!lexicon && (
          <TotalRewards
            spAddress={spAddress}
            collateralGain={collateralGain}
            collateralGainUSDValue={collateralGainUSDValue}
            psyReward={stabilityDeposit.psyReward}
            lexicon={lexicon}
            className="md:ml-2 flex-1"
          />
        )}
      </div>
      <div className="mt-3">
        <StabilityDepositEditor
          originalDeposit={originalDeposit}
          editedSLSD={editedSLSD}
          changePending={changePending}
          dispatch={dispatch}
          description={description}
          validChange={validChange}
          transactionId={transactionId}
          spAddress={spAddress}
        />
      </div>
    </div>
  );
};
